import { useEffect, useState } from 'react';
import { MCP_LOGOS } from '../RemoteMCPAction/mcpLogos';
import { MONOGRAM_PREFIX, monogramLabel, resolveActionIcon } from './resolveActionIcon';

const LOGO_PREFIX = 'logo:';

const fallbackLetter = (icon: string, name?: string): string => {
  if (name?.trim()) return name.trim().charAt(0).toUpperCase();
  if (icon.startsWith(LOGO_PREFIX)) {
    const parts = icon.split(':'); // ['logo', <domain>, <letter>]
    return (parts[2] || parts[1] || '').charAt(0).toUpperCase();
  }
  return monogramLabel(icon);
};

type Props = { icon: string; name?: string; size: number; color?: string };

/** Renders an action/catalog brand icon: a react-icons Font Awesome icon (icon picker) if the name
 *  resolves to one, otherwise the brand's self-hosted logo (`logo:<domain>` marker -> committed
 *  file in mcpLogos), with a letter monogram as the final fallback if there is no logo or it fails
 *  to load. Shared by the catalog tile and the action-card icon so they always match. */
export const BrandGlyph = ({ icon, name, size, color }: Props) => {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => setImgFailed(false), [icon]);

  const ReactIcon = resolveActionIcon(icon);
  if (ReactIcon) return <ReactIcon size={size} color={color} />;

  if (icon.startsWith(LOGO_PREFIX) && !imgFailed) {
    const domain = icon.slice(LOGO_PREFIX.length).split(':')[0];
    const src = MCP_LOGOS[domain];
    if (src) {
      return (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          className="rounded-[4px] object-contain"
          onError={() => setImgFailed(true)}
        />
      );
    }
  }

  return (
    <span className="text-sm font-semibold leading-none" style={{ color }}>
      {fallbackLetter(icon, name) || (icon.startsWith(MONOGRAM_PREFIX) ? monogramLabel(icon) : '')}
    </span>
  );
};
